import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import Download from './components/Download';
import Features from './components/Features';
import Footer from './components/Footer';
import Header from './components/Header';
import Hero from './components/Hero';
import MeetingAssistant from './components/MeetingAssistant';
import AgentOfficeApplicationDialog, {
  ContactIntent,
} from './components/AgentOfficeApplicationDialog';
import Workflow from './components/Workflow';
import AndroidNotificationGuidePage from './h5/android-notification-guide/AndroidNotificationGuidePage';
import { ThemeMode, useThemeMode } from './theme/themeMode';

/** 组件统一用 CSS 令牌取色，这里只补 MUI 调色板 */
const createAppTheme = (mode: ThemeMode): Theme =>
  createTheme({
    palette: {
      mode,
      primary: { main: mode === 'dark' ? '#68d2ba' : '#126c62' },
      secondary: { main: mode === 'dark' ? '#ff9d67' : '#d36432' },
      background: {
        default: mode === 'dark' ? '#091617' : '#f3f1ea',
        paper: mode === 'dark' ? '#112223' : '#faf9f4',
      },
      text: {
        primary: mode === 'dark' ? '#e7efe9' : '#132a2b',
        secondary: mode === 'dark' ? 'rgba(231, 239, 233, 0.72)' : 'rgba(19, 42, 43, 0.66)',
      },
      divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.13)' : 'rgba(19, 42, 43, 0.11)',
    },
    shape: { borderRadius: 16 },
    typography: {
      button: { textTransform: 'none', fontWeight: 600 },
    },
  });

interface HomePageProps {
  mode: ThemeMode;
  onToggleTheme: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ mode, onToggleTheme }) => {
  const [contactIntent, setContactIntent] = React.useState<ContactIntent | null>(
    null
  );
  const openContact = (intent: ContactIntent) => setContactIntent(intent);

  return (
    <>
      <Header
        mode={mode}
        onToggleTheme={onToggleTheme}
        onContact={() => openContact('contact')}
      />
      <Hero onContact={() => openContact('reservation')} />
    <Features />
    <Workflow />
    <MeetingAssistant />
      <Download onContact={() => openContact('contact')} />
      <Footer onContact={() => openContact('contact')} />
      <AgentOfficeApplicationDialog
        open={contactIntent !== null}
        intent={contactIntent || 'contact'}
        onClose={() => setContactIntent(null)}
      />
    </>
  );
};

const App: React.FC = () => {
  const { mode, toggle } = useThemeMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<HomePage mode={mode} onToggleTheme={toggle} />} />
        <Route path="/h5/android-notification-guide" element={<AndroidNotificationGuidePage />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
